import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import {
	CheckSquare,
	ChevronDown,
	ChevronRight,
	User,
	Calendar,
	Edit2,
	ListTodo,
	FileText,
	Search,
	Shield,
	AlertCircle,
	ExternalLink,
	MessageSquare,
	Paperclip,
	Plus,
	CheckCircle,
	XCircle,
	Clock,
	RotateCcw,
	BookOpen,
} from 'lucide-react'
import { Button, Input, Modal } from '@components/common'
import { ApprovalStatusBadge } from '@components/features/projects'
import { TaskConfigurationModal } from '@components/features/tasks'
import { WorkflowStepBuilder, WORKFLOW_ROLES } from '@components/features/workflows'
import checklistService from '@services/checklistService'
import workflowService from '@services/workflowService'
import organizationDocumentService from '@services/organizationDocumentService'
import { useAuth } from '@context/AuthContext'

const ROLE_LABEL = {
	...Object.fromEntries(WORKFLOW_ROLES.map((r) => [r.value, r.label])),
	'executive': 'Executive',
	'management': 'Management',
	'super-admin': 'Super Admin',
	'employee': 'Employee',
	'compliance-officer': 'Compliance Officer',
	'risk-manager': 'Risk Manager',
}

const buildWorkflowLabel = (wf) => {
	if (!wf.steps_summary) return wf.name
	return wf.steps_summary.split('|').join(' → ')
}

/**
 * ChecklistsTab Component
 *
 * Displays project checklists grouped by certificate/standard
 * with detailed information, actions, and task navigation.
 *
 * Approval flow for manually created items (CM only):
 *   pending_pm → PM approves/rejects
 *   pending_management → Management approves/rejects
 *   approved → visible in active checklist; Management can revoke
 *   revoked / rejected → flagged, no further action
 */
const ChecklistsTab = ({
	checklists,
	project,
	certificates,
	onItemUpdate,
	canEdit,
	canAddChecklist,
	canApproveChecklist,
	canPmReviewChecklist,
	canManagementReviewChecklist,
	canRevokeChecklist,
	onRefresh,
}) => {
	const navigate = useNavigate()
	const { user } = useAuth()
	const [expandedGroups, setExpandedGroups] = useState({})
	const [activeFilter, setActiveFilter] = useState('all')
	const [searchQuery, setSearchQuery] = useState('')
	const [expandedItems, setExpandedItems] = useState({})

	// Task configuration modal state
	const [configModalOpen, setConfigModalOpen] = useState(false)
	const [selectedTask, setSelectedTask] = useState(null)

	// Add checklist modal state
	const [addModalOpen, setAddModalOpen] = useState(false)
	const [addForm, setAddForm] = useState({
		title: '',
		description: '',
		certificate_id: '',
		planned_start_date: '',
		planned_end_date: '',
		workflow_id: '',
		organization_document_id: '',
		evidence_required: false,
	})
	const [addLoading, setAddLoading] = useState(false)
	const [addError, setAddError] = useState(null)
	const [workflows, setWorkflows] = useState([])
	const [workflowsLoading, setWorkflowsLoading] = useState(false)
	const [orgDocuments, setOrgDocuments] = useState([])
	const [orgDocSearch, setOrgDocSearch] = useState('')

	// Add workflow modal state
	const [addWorkflowModalOpen, setAddWorkflowModalOpen] = useState(false)
	const [workflowForm, setWorkflowForm] = useState({ name: '', description: '' })
	const [workflowSteps, setWorkflowSteps] = useState([])
	const [workflowFormLoading, setWorkflowFormLoading] = useState(false)
	const [workflowFormError, setWorkflowFormError] = useState(null)
	const [companyUsers, setCompanyUsers] = useState([])

	// Legacy approval action state (Company Admin / Super Admin)
	const [approvalAction, setApprovalAction] = useState(null) // { item, type: 'approve'|'reject' }
	const [approvalNote, setApprovalNote] = useState('')
	const [approvalLoading, setApprovalLoading] = useState(false)
	const [approvalError, setApprovalError] = useState(null)

	// PM review modal state
	const [pmReviewAction, setPmReviewAction] = useState(null) // { item, type: 'approve'|'reject' }
	const [pmReviewNote, setPmReviewNote] = useState('')
	const [pmReviewLoading, setPmReviewLoading] = useState(false)
	const [pmReviewError, setPmReviewError] = useState(null)

	// Management review modal state
	const [mgmtReviewAction, setMgmtReviewAction] = useState(null) // { item, type: 'approve'|'reject' }
	const [mgmtReviewNote, setMgmtReviewNote] = useState('')
	const [mgmtReviewLoading, setMgmtReviewLoading] = useState(false)
	const [mgmtReviewError, setMgmtReviewError] = useState(null)

	// Revoke modal state
	const [revokeTarget, setRevokeTarget] = useState(null) // item
	const [revokeNote, setRevokeNote] = useState('')
	const [revokeLoading, setRevokeLoading] = useState(false)
	const [revokeError, setRevokeError] = useState(null)

	// Check if user can configure tasks (CM/PM/Admin)
	const canConfigureTasks = useMemo(() => {
		if (!user) return false
		return ['super-admin', 'company-admin', 'compliance-manager', 'project-manager'].includes(user.role?.slug)
	}, [user])

	// Filter options
	const filterOptions = [
		{ value: 'all', label: 'All', count: checklists.length },
		{
			value: 'not_started',
			label: 'Not Started',
			count: checklists.filter((i) => i.workflow_status === 'not_started').length,
		},
		{
			value: 'in_progress',
			label: 'In Progress',
			count: checklists.filter((i) => i.workflow_status === 'in_progress').length,
		},
		{
			value: 'pending_review',
			label: 'Pending Review',
			count: checklists.filter((i) => i.workflow_status === 'pending_review').length,
		},
		{
			value: 'approved',
			label: 'Approved',
			count: checklists.filter((i) => i.workflow_status === 'approved').length,
		},
	]

	// Filter and search checklists
	const filteredChecklists = useMemo(() => {
		let filtered = checklists

		if (activeFilter !== 'all') {
			filtered = filtered.filter((item) => item.workflow_status === activeFilter)
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			filtered = filtered.filter(
				(item) =>
					(item.title || item.item_text || '').toLowerCase().includes(query) ||
					(item.description || '').toLowerCase().includes(query)
			)
		}

		return filtered
	}, [checklists, activeFilter, searchQuery])

	// Group by certificate
	const groupedChecklists = useMemo(() => {
		const groups = {}

		filteredChecklists.forEach((item) => {
			const groupKey = item.certificate_title || item.certificate_name || item.certificate_id || 'General'
			if (!groups[groupKey]) {
				const cert = certificates.find(
					(c) =>
						c.id === item.certificate_id ||
						c.name === item.certificate_name ||
						c.title === item.certificate_title
				)
				groups[groupKey] = {
					name: groupKey,
					certificate: cert,
					items: [],
					completedCount: 0,
					totalCount: 0,
				}
			}
			groups[groupKey].items.push(item)
			groups[groupKey].totalCount++
			if (['approved', 'not_applicable'].includes(item.workflow_status)) {
				groups[groupKey].completedCount++
			}
		})

		return Object.values(groups)
	}, [filteredChecklists, certificates])

	const toggleGroup = (groupName) => {
		setExpandedGroups((prev) => ({
			...prev,
			[groupName]: prev[groupName] === false ? true : false,
		}))
	}

	const toggleItemExpand = (itemId) => {
		setExpandedItems((prev) => ({
			...prev,
			[itemId]: !prev[itemId],
		}))
	}

	const formatDate = (dateString) => {
		if (!dateString) return null
		const date = new Date(dateString)
		const today = new Date()
		const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24))

		if (diffDays < 0) {
			return { text: `${Math.abs(diffDays)} days overdue`, isOverdue: true }
		} else if (diffDays === 0) {
			return { text: 'Due today', isOverdue: false }
		} else {
			return { text: `Due in ${diffDays} days`, isOverdue: false }
		}
	}

	const getStatusClass = (status) => {
		return `checklist-status checklist-status--${status || 'not_started'}`
	}

	/**
	 * Return badge config for approval_stage value
	 */
	const getApprovalStageBadge = (stage) => {
		switch (stage) {
			case 'pending_pm':
				return { label: 'Awaiting PM Review', cls: 'checklist-approval-badge--pending', Icon: Clock }
			case 'pending_management':
				return { label: 'Awaiting Management', cls: 'checklist-approval-badge--warning', Icon: Clock }
			case 'revoked':
				return { label: 'Revoked', cls: 'checklist-approval-badge--rejected', Icon: XCircle }
			case 'rejected':
				return { label: 'Rejected', cls: 'checklist-approval-badge--rejected', Icon: XCircle }
			case 'approved':
			default:
				return null
		}
	}

	const handleViewDetails = (item) => {
		navigate(`/tasks/${item.id}`)
	}

	const handleConfigureTask = (item) => {
		setSelectedTask(item)
		setConfigModalOpen(true)
	}

	const handleTaskConfigSave = () => {
		if (onRefresh) onRefresh()
	}

	// ── Add checklist ──────────────────────────────────────────────────────
	const handleOpenAddModal = async () => {
		setAddForm({
			title: '',
			description: '',
			certificate_id: '',
			planned_start_date: '',
			planned_end_date: '',
			workflow_id: '',
			organization_document_id: '',
			evidence_required: false,
		})
		setOrgDocSearch('')
		setAddError(null)
		setAddModalOpen(true)

		setWorkflowsLoading(true)
		try {
			// Load workflows and org documents in parallel
			const [wfRes, orgDocRes] = await Promise.all([
				workflowService.getActive(),
				organizationDocumentService.getAll(),
			])

			const basicList = wfRes?.data ?? []
			const enriched = await Promise.all(
				basicList.map(async (wf) => {
					try {
						const detail = await workflowService.getById(wf.id)
						const steps = (detail?.data?.steps ?? []).sort((a, b) => a.step_number - b.step_number)
						const stepsSummary = steps.map((s) => s.role_name || ROLE_LABEL[s.role] || s.role).join('|')
						return { ...wf, steps_summary: stepsSummary || null }
					} catch {
						return wf
					}
				})
			)
			setWorkflows(enriched)
			setOrgDocuments(orgDocRes?.data ?? [])
		} catch {
			setWorkflows([])
			setOrgDocuments([])
		} finally {
			setWorkflowsLoading(false)
		}
	}

	const handleAddChecklist = async (e) => {
		e.preventDefault()
		if (!addForm.title.trim()) {
			setAddError('Title is required.')
			return
		}
		if (!addForm.planned_start_date) {
			setAddError('Planned start date is required.')
			return
		}
		if (!addForm.planned_end_date) {
			setAddError('Planned end date is required.')
			return
		}
		if (addForm.planned_end_date < addForm.planned_start_date) {
			setAddError('Planned end date must be on or after the planned start date.')
			return
		}
		setAddLoading(true)
		setAddError(null)
		try {
			await checklistService.create({
				project_id: project.id,
				title: addForm.title.trim(),
				description: addForm.description.trim() || null,
				certificate_id: addForm.certificate_id || null,
				planned_start_date: addForm.planned_start_date,
				planned_end_date: addForm.planned_end_date,
				workflow_id: addForm.workflow_id || null,
				organization_document_id: addForm.organization_document_id || null,
				evidence_required: addForm.evidence_required ? 1 : 0,
			})
			setAddModalOpen(false)
			if (onRefresh) onRefresh()
		} catch (err) {
			setAddError(err.message || 'Failed to create checklist item.')
		} finally {
			setAddLoading(false)
		}
	}

	// ── Add workflow (inline creation) ────────────────────────────────────
	const handleOpenAddWorkflowModal = async () => {
		setWorkflowForm({ name: '', description: '' })
		setWorkflowSteps([])
		setWorkflowFormError(null)
		setAddWorkflowModalOpen(true)

		try {
			const res = await workflowService.getCompanyUsers(user?.company_id)
			setCompanyUsers(res?.data ?? [])
		} catch {
			setCompanyUsers([])
		}
	}

	const handleCreateWorkflow = async () => {
		if (!workflowForm.name.trim()) {
			setWorkflowFormError('Workflow name is required.')
			return
		}
		if (workflowSteps.length === 0) {
			setWorkflowFormError('At least one step is required.')
			return
		}
		const stepsWithoutRole = workflowSteps.filter((s) => !s.role)
		if (stepsWithoutRole.length > 0) {
			setWorkflowFormError('All steps must have a role selected.')
			return
		}

		setWorkflowFormLoading(true)
		setWorkflowFormError(null)
		try {
			const res = await workflowService.create({
				company_id: user?.company_id,
				name: workflowForm.name.trim(),
				description: workflowForm.description.trim() || null,
				steps: workflowSteps.map((step) => ({
					name: step.name || null,
					role: step.role,
					approver_id: step.approver_id || null,
					sla_hours: step.sla_hours || 24,
					evidence_required: step.evidence_required ? 1 : 0,
				})),
			})
			const newWorkflow = res?.data
			if (newWorkflow) {
				const stepsSummary = workflowSteps.map((s) => ROLE_LABEL[s.role] || s.role).join('|')
				setWorkflows((prev) => [...prev, { ...newWorkflow, steps_count: workflowSteps.length, steps_summary: stepsSummary }])
				setAddForm((prev) => ({ ...prev, workflow_id: String(newWorkflow.id) }))
			}
			setAddWorkflowModalOpen(false)
		} catch (err) {
			setWorkflowFormError(err.message || 'Failed to create workflow.')
		} finally {
			setWorkflowFormLoading(false)
		}
	}

	// ── Legacy approval (Company Admin / Super Admin) ──────────────────────
	const handleApprovalSubmit = async () => {
		if (!approvalAction) return
		setApprovalLoading(true)
		setApprovalError(null)
		try {
			if (approvalAction.type === 'approve') {
				await checklistService.approve(approvalAction.item.id, approvalNote)
			} else {
				await checklistService.reject(approvalAction.item.id, approvalNote)
			}
			setApprovalAction(null)
			setApprovalNote('')
			if (onRefresh) onRefresh()
		} catch (err) {
			setApprovalError(err.message || 'Action failed. Please try again.')
		} finally {
			setApprovalLoading(false)
		}
	}

	// ── PM review ──────────────────────────────────────────────────────────
	const handlePmReviewSubmit = async () => {
		if (!pmReviewAction) return
		if (pmReviewAction.type === 'reject' && !pmReviewNote.trim()) {
			setPmReviewError('Please provide a reason for rejection.')
			return
		}
		setPmReviewLoading(true)
		setPmReviewError(null)
		try {
			await checklistService.pmReview(pmReviewAction.item.id, pmReviewAction.type, pmReviewNote)
			setPmReviewAction(null)
			setPmReviewNote('')
			if (onRefresh) onRefresh()
		} catch (err) {
			setPmReviewError(err.message || 'Action failed. Please try again.')
		} finally {
			setPmReviewLoading(false)
		}
	}

	// ── Management review ─────────────────────────────────────────────────
	const handleMgmtReviewSubmit = async () => {
		if (!mgmtReviewAction) return
		if (mgmtReviewAction.type === 'reject' && !mgmtReviewNote.trim()) {
			setMgmtReviewError('Please provide a reason for rejection.')
			return
		}
		setMgmtReviewLoading(true)
		setMgmtReviewError(null)
		try {
			await checklistService.managementReview(mgmtReviewAction.item.id, mgmtReviewAction.type, mgmtReviewNote)
			setMgmtReviewAction(null)
			setMgmtReviewNote('')
			if (onRefresh) onRefresh()
		} catch (err) {
			setMgmtReviewError(err.message || 'Action failed. Please try again.')
		} finally {
			setMgmtReviewLoading(false)
		}
	}

	// ── Revoke ────────────────────────────────────────────────────────────
	const handleRevokeSubmit = async () => {
		if (!revokeTarget) return
		setRevokeLoading(true)
		setRevokeError(null)
		try {
			await checklistService.revoke(revokeTarget.id, revokeNote)
			setRevokeTarget(null)
			setRevokeNote('')
			if (onRefresh) onRefresh()
		} catch (err) {
			setRevokeError(err.message || 'Failed to revoke. Please try again.')
		} finally {
			setRevokeLoading(false)
		}
	}

	// ── Render each checklist card ─────────────────────────────────────────
	const renderChecklistItem = (item) => {
		const dueInfo    = formatDate(item.due_date)
		const isExpanded = expandedItems[item.id]
		const stageBadge = getApprovalStageBadge(item.approval_stage)

		return (
			<div key={item.id} className={`checklist-card ${isExpanded ? 'checklist-card--expanded' : ''}`}>
				<div className="checklist-card__main">
					<div className="checklist-card__content" onClick={() => toggleItemExpand(item.id)}>
						<div className="checklist-card__header">
							<h4 className="checklist-card__title">
								{item.title || item.item_text}
							</h4>
							<div className="checklist-card__badges">
								{/* Approval stage badge (new flow) */}
								{stageBadge && (
									<span className={`checklist-approval-badge ${stageBadge.cls}`}>
										<stageBadge.Icon size={11} />
										{stageBadge.label}
									</span>
								)}

								{/* Legacy approval_status badge */}
								{!item.approval_stage && item.approval_status === 'pending_approval' && (
									<span className="checklist-approval-badge checklist-approval-badge--pending">
										<Clock size={11} />
										Pending Approval
									</span>
								)}
								{!item.approval_stage && item.approval_status === 'rejected' && (
									<span className="checklist-approval-badge checklist-approval-badge--rejected">
										<XCircle size={11} />
										Rejected
									</span>
								)}
								{!item.approval_stage && item.approval_status &&
									!['pending_approval', 'rejected', 'pending'].includes(item.approval_status) && (
										<ApprovalStatusBadge status={item.approval_status} size="small" />
									)}

								<span className={getStatusClass(item.workflow_status)}>
									{checklistService.getStatusLabel(item.workflow_status)}
								</span>
							</div>
						</div>

						<div className="checklist-card__meta">
							{dueInfo ? (
								<span className={`checklist-card__due ${dueInfo.isOverdue ? 'checklist-card__due--overdue' : ''}`}>
									<Calendar size={12} />
									{dueInfo.text}
								</span>
							) : item.planned_start_date ? (
								<span className="checklist-card__due">
									<Calendar size={12} />
									{item.planned_start_date} → {item.planned_end_date || '—'}
								</span>
							) : (
								<span className="checklist-card__due checklist-card__due--not-set">
									<AlertCircle size={12} />
									Due date not set
								</span>
							)}
							{item.assigned_to_name && (
								<span className="checklist-card__assignee">
									<User size={12} />
									{item.assigned_to_name}
								</span>
							)}
							{item.workflow_name && (
								<span className="checklist-card__workflow">
									<FileText size={12} />
									{item.workflow_name}
								</span>
							)}
						</div>
					</div>

					<div className="checklist-card__actions">
						<button
							className="checklist-card__expand-btn"
							onClick={() => toggleItemExpand(item.id)}
							title={isExpanded ? 'Collapse' : 'Expand'}
						>
							{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
						</button>
					</div>
				</div>

				{/* Expanded Details */}
				{isExpanded && (
					<div className="checklist-card__details">
						{item.description && (
							<div className="checklist-card__description">
								<h5>Description</h5>
								<p>{item.description}</p>
							</div>
						)}

						{/* Planned dates (shown when set) */}
						{(item.planned_start_date || item.planned_end_date) && (
							<div className="checklist-card__description">
								<h5>Planned Schedule</h5>
								<p>
									<Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />
									{item.planned_start_date || '—'} &rarr; {item.planned_end_date || '—'}
								</p>
							</div>
						)}

						{/* PM review notes */}
						{item.pm_review_notes && (
							<div className="checklist-card__excuse">
								<AlertCircle size={16} />
								<div>
									<strong>PM Notes:</strong>
									<span>{item.pm_review_notes}</span>
								</div>
							</div>
						)}

						{/* Management review notes */}
						{item.management_review_notes && (
							<div className="checklist-card__excuse">
								<AlertCircle size={16} />
								<div>
									<strong>Management Notes:</strong>
									<span>{item.management_review_notes}</span>
								</div>
							</div>
						)}

						{item.excuse_reason && (
							<div className="checklist-card__excuse">
								<AlertCircle size={16} />
								<div>
									<strong>Excused:</strong>
									<span>{item.excuse_reason}</span>
								</div>
							</div>
						)}

						{item.organization_document_title && (
							<div className="checklist-card__org-doc">
								<BookOpen size={14} />
								<span>Reference Document:</span>
								<a
									href={item.organization_document_drive_link}
									target="_blank"
									rel="noopener noreferrer"
									className="checklist-card__org-doc-link"
								>
									{item.organization_document_title}
									<ExternalLink size={11} />
								</a>
							</div>
						)}

						{(item.evidence_required || item.notes_count > 0 || item.documents_count > 0) && (
							<div className="checklist-card__task-meta">
								{item.evidence_required === 1 && (
									<span className="task-meta-badge task-meta-badge--required">
										<Paperclip size={12} />
										Evidence Required
									</span>
								)}
								{item.notes_count > 0 && (
									<span className="task-meta-badge">
										<MessageSquare size={12} />
										{item.notes_count} notes
									</span>
								)}
								{item.documents_count > 0 && (
									<span className="task-meta-badge">
										<Paperclip size={12} />
										{item.documents_count} docs
									</span>
								)}
							</div>
						)}

						<div className="checklist-card__detail-actions">
							<Button variant="primary" size="small" onClick={() => handleViewDetails(item)}>
								<ListTodo size={14} />
								View Details
								<ExternalLink size={12} />
							</Button>

							{(canConfigureTasks || canEdit) && (
								<Button
									variant="ghost"
									size="small"
									onClick={() => handleConfigureTask(item)}
									title="Edit task settings"
								>
									<Edit2 size={14} />
									Edit
								</Button>
							)}

							{/* Legacy approval buttons (Company Admin / Super Admin) */}
							{canApproveChecklist && item.approval_status === 'pending_approval' && !item.approval_stage && (
								<>
									<Button
										variant="success"
										size="small"
										onClick={() => { setApprovalAction({ item, type: 'approve' }); setApprovalNote('') }}
									>
										<CheckCircle size={14} />
										Approve
									</Button>
									<Button
										variant="danger"
										size="small"
										onClick={() => { setApprovalAction({ item, type: 'reject' }); setApprovalNote('') }}
									>
										<XCircle size={14} />
										Reject
									</Button>
								</>
							)}

							{/* PM review buttons */}
							{canPmReviewChecklist && item.approval_stage === 'pending_pm' && (
								<>
									<Button
										variant="success"
										size="small"
										onClick={() => { setPmReviewAction({ item, type: 'approve' }); setPmReviewNote('') }}
									>
										<CheckCircle size={14} />
										Approve
									</Button>
									<Button
										variant="danger"
										size="small"
										onClick={() => { setPmReviewAction({ item, type: 'reject' }); setPmReviewNote('') }}
									>
										<XCircle size={14} />
										Reject
									</Button>
								</>
							)}

							{/* Management review buttons */}
							{canManagementReviewChecklist && item.approval_stage === 'pending_management' && (
								<>
									<Button
										variant="success"
										size="small"
										onClick={() => { setMgmtReviewAction({ item, type: 'approve' }); setMgmtReviewNote('') }}
									>
										<CheckCircle size={14} />
										Approve
									</Button>
									<Button
										variant="danger"
										size="small"
										onClick={() => { setMgmtReviewAction({ item, type: 'reject' }); setMgmtReviewNote('') }}
									>
										<XCircle size={14} />
										Reject
									</Button>
								</>
							)}

							{/* Revoke button (Management, approved items only) */}
							{canRevokeChecklist && item.approval_stage === 'approved' && (
								<Button
									variant="danger"
									size="small"
									onClick={() => { setRevokeTarget(item); setRevokeNote('') }}
								>
									<RotateCcw size={14} />
									Revoke
								</Button>
							)}
						</div>
					</div>
				)}
			</div>
		)
	}

	return (
		<div className="checklists-tab">
			{/* Toolbar */}
			<div className="checklists-tab__toolbar">
				<div className="checklists-tab__search">
					<Search size={16} />
					<input
						type="text"
						placeholder="Search checklists..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>

				<div className="checklists-tab__filters">
					{filterOptions.map((option) => (
						<button
							key={option.value}
							className={`filter-chip ${activeFilter === option.value ? 'filter-chip--active' : ''}`}
							onClick={() => setActiveFilter(option.value)}
						>
							{option.label}
							<span className="filter-chip__count">{option.count}</span>
						</button>
					))}
				</div>

				{canAddChecklist && (
					<Button variant="primary" size="small" onClick={handleOpenAddModal}>
						<Plus size={16} />
						Add Checklist
					</Button>
				)}
			</div>

			{/* Summary Bar */}
			<div className="checklists-tab__summary">
				<div className="summary-stat">
					<span className="summary-stat__value">{filteredChecklists.length}</span>
					<span className="summary-stat__label">
						{activeFilter === 'all' ? 'Total Items' : filterOptions.find((f) => f.value === activeFilter)?.label}
					</span>
				</div>
				<div className="summary-stat summary-stat--certificates">
					<span className="summary-stat__value">{groupedChecklists.length}</span>
					<span className="summary-stat__label">Standards</span>
				</div>
			</div>

			{/* Checklist Groups */}
			{filteredChecklists.length > 0 ? (
				<div className="checklists-tab__groups">
					{groupedChecklists.map((group) => {
						const isExpanded = expandedGroups[group.name] !== false
						const progress =
							group.totalCount > 0
								? Math.round((group.completedCount / group.totalCount) * 100)
								: 0

						return (
							<div key={group.name} className="checklist-group">
								<button
									className="checklist-group__header"
									onClick={() => toggleGroup(group.name)}
								>
									<div className="checklist-group__left">
										<span className="checklist-group__chevron">
											{isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
										</span>
										<div className="checklist-group__icon">
											<Shield size={18} />
										</div>
										<div className="checklist-group__info">
											<span className="checklist-group__name">{group.name}</span>
											<span className="checklist-group__count">
												{group.completedCount} / {group.totalCount} completed
											</span>
										</div>
									</div>

									<div className="checklist-group__right">
										<div className="checklist-group__progress">
											<div className="checklist-group__progress-bar">
												<div
													className="checklist-group__progress-fill"
													style={{ width: `${progress}%` }}
												/>
											</div>
											<span className="checklist-group__progress-text">{progress}%</span>
										</div>
									</div>
								</button>

								{isExpanded && (
									<div className="checklist-group__items">
										{group.items.map(renderChecklistItem)}
									</div>
								)}
							</div>
						)
					})}
				</div>
			) : (
				<div className="checklists-tab__empty">
					<CheckSquare size={48} />
					<h4>No Checklist Items</h4>
					<p>
						{searchQuery || activeFilter !== 'all'
							? 'No items match your search or filter criteria.'
							: 'No checklist items have been added to this project yet.'}
					</p>
					{(searchQuery || activeFilter !== 'all') && (
						<Button
							variant="outline"
							size="small"
							onClick={() => {
								setSearchQuery('')
								setActiveFilter('all')
							}}
						>
							Clear Filters
						</Button>
					)}
				</div>
			)}

			{/* Task Configuration Modal */}
			<TaskConfigurationModal
				isOpen={configModalOpen}
				onClose={() => {
					setConfigModalOpen(false)
					setSelectedTask(null)
				}}
				task={selectedTask}
				onSave={handleTaskConfigSave}
			/>

			{/* ── Legacy Approve / Reject Modal (Company Admin / Super Admin) ── */}
			<Modal
				isOpen={!!approvalAction}
				onClose={() => { setApprovalAction(null); setApprovalNote('') }}
				title={approvalAction?.type === 'approve' ? 'Approve Checklist Item' : 'Reject Checklist Item'}
				size="small"
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => { setApprovalAction(null); setApprovalNote('') }} disabled={approvalLoading}>
							Cancel
						</Button>
						<Button
							variant={approvalAction?.type === 'approve' ? 'success' : 'danger'}
							size="small"
							onClick={handleApprovalSubmit}
							disabled={approvalLoading}
						>
							{approvalLoading ? 'Saving...' : approvalAction?.type === 'approve' ? 'Approve' : 'Reject'}
						</Button>
					</div>
				}
			>
				<div className="approval-confirm-modal">
					{approvalError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{approvalError}
						</div>
					)}
					<p className="approval-confirm-modal__item-name">
						<strong>{approvalAction?.item?.title}</strong>
					</p>
					<div className="add-checklist-form__field">
						<label>{approvalAction?.type === 'approve' ? 'Notes (optional)' : 'Reason for rejection'}</label>
						<textarea
							rows={3}
							placeholder={approvalAction?.type === 'approve' ? 'Add approval notes...' : 'Explain why this item is being rejected...'}
							value={approvalNote}
							onChange={(e) => setApprovalNote(e.target.value)}
						/>
					</div>
				</div>
			</Modal>

			{/* ── PM Review Modal ─────────────────────────────────────────────── */}
			<Modal
				isOpen={!!pmReviewAction}
				onClose={() => { setPmReviewAction(null); setPmReviewNote('') }}
				title={pmReviewAction?.type === 'approve' ? 'Approve Checklist Item' : 'Reject Checklist Item'}
				size="medium"
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => { setPmReviewAction(null); setPmReviewNote('') }} disabled={pmReviewLoading}>
							Cancel
						</Button>
						<Button
							variant={pmReviewAction?.type === 'approve' ? 'success' : 'danger'}
							size="small"
							onClick={handlePmReviewSubmit}
							disabled={pmReviewLoading}
						>
							{pmReviewLoading ? 'Saving...' : pmReviewAction?.type === 'approve' ? 'Approve' : 'Reject'}
						</Button>
					</div>
				}
			>
				<div className="approval-confirm-modal">
					{pmReviewError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{pmReviewError}
						</div>
					)}
					<p className="approval-confirm-modal__item-name">
						<strong>{pmReviewAction?.item?.title}</strong>
					</p>
					{pmReviewAction?.item?.description && (
						<p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '13px' }}>
							{pmReviewAction.item.description}
						</p>
					)}
					{(pmReviewAction?.item?.planned_start_date || pmReviewAction?.item?.planned_end_date) && (
						<p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
							<Calendar size={12} style={{ display: 'inline', marginRight: 4 }} />
							{pmReviewAction?.item?.planned_start_date} &rarr; {pmReviewAction?.item?.planned_end_date || '—'}
						</p>
					)}
					<div className="add-checklist-form__field">
						<label>
							{pmReviewAction?.type === 'approve' ? 'Notes (optional)' : 'Reason for rejection *'}
						</label>
						<textarea
							rows={3}
							placeholder={
								pmReviewAction?.type === 'approve'
									? 'Add any notes for this approval...'
									: 'Explain why this item cannot be approved (required)...'
							}
							value={pmReviewNote}
							onChange={(e) => setPmReviewNote(e.target.value)}
						/>
					</div>
					{pmReviewAction?.type === 'reject' && (
						<p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
							Rejecting will escalate this item to Management for a final decision.
						</p>
					)}
				</div>
			</Modal>

			{/* ── Management Review Modal ──────────────────────────────────────── */}
			<Modal
				isOpen={!!mgmtReviewAction}
				onClose={() => { setMgmtReviewAction(null); setMgmtReviewNote('') }}
				title={mgmtReviewAction?.type === 'approve' ? 'Approve Checklist Item' : 'Reject Checklist Item'}
				size="medium"
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => { setMgmtReviewAction(null); setMgmtReviewNote('') }} disabled={mgmtReviewLoading}>
							Cancel
						</Button>
						<Button
							variant={mgmtReviewAction?.type === 'approve' ? 'success' : 'danger'}
							size="small"
							onClick={handleMgmtReviewSubmit}
							disabled={mgmtReviewLoading}
						>
							{mgmtReviewLoading ? 'Saving...' : mgmtReviewAction?.type === 'approve' ? 'Approve' : 'Reject'}
						</Button>
					</div>
				}
			>
				<div className="approval-confirm-modal">
					{mgmtReviewError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{mgmtReviewError}
						</div>
					)}
					<p className="approval-confirm-modal__item-name">
						<strong>{mgmtReviewAction?.item?.title}</strong>
					</p>
					{mgmtReviewAction?.item?.description && (
						<p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '13px' }}>
							{mgmtReviewAction.item.description}
						</p>
					)}
					{mgmtReviewAction?.item?.pm_review_notes && (
						<div className="add-checklist-form__field">
							<label>PM's Notes</label>
							<p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
								{mgmtReviewAction.item.pm_review_notes}
							</p>
						</div>
					)}
					<div className="add-checklist-form__field">
						<label>
							{mgmtReviewAction?.type === 'approve' ? 'Decision Notes (optional)' : 'Reason for rejection *'}
						</label>
						<textarea
							rows={3}
							placeholder={
								mgmtReviewAction?.type === 'approve'
									? 'Add any notes for this approval...'
									: 'Explain the final rejection reason (required)...'
							}
							value={mgmtReviewNote}
							onChange={(e) => setMgmtReviewNote(e.target.value)}
						/>
					</div>
					{mgmtReviewAction?.type === 'reject' && (
						<p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
							Only the PM and Compliance Manager will be notified of this rejection.
						</p>
					)}
				</div>
			</Modal>

			{/* ── Revoke Confirmation Modal ─────────────────────────────────────── */}
			<Modal
				isOpen={!!revokeTarget}
				onClose={() => { setRevokeTarget(null); setRevokeNote('') }}
				title="Revoke Checklist Item"
				size="small"
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => { setRevokeTarget(null); setRevokeNote('') }} disabled={revokeLoading}>
							Cancel
						</Button>
						<Button
							variant="danger"
							size="small"
							onClick={handleRevokeSubmit}
							disabled={revokeLoading}
						>
							{revokeLoading ? 'Revoking...' : 'Revoke'}
						</Button>
					</div>
				}
			>
				<div className="approval-confirm-modal">
					{revokeError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{revokeError}
						</div>
					)}
					<p className="approval-confirm-modal__item-name">
						<strong>{revokeTarget?.title}</strong>
					</p>
					<p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
						Revoking this item will remove it from the active project checklist. The PM and Compliance Manager will be notified.
					</p>
					<div className="add-checklist-form__field">
						<label>Reason for revocation (optional)</label>
						<textarea
							rows={3}
							placeholder="Explain why this checklist item is being revoked..."
							value={revokeNote}
							onChange={(e) => setRevokeNote(e.target.value)}
						/>
					</div>
				</div>
			</Modal>

			{/* ── Add Workflow Modal ────────────────────────────────────────────── */}
			<Modal
				isOpen={addWorkflowModalOpen}
				onClose={() => setAddWorkflowModalOpen(false)}
				title="Add Workflow"
				size="large"
				closeOnBackdrop={false}
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => setAddWorkflowModalOpen(false)} disabled={workflowFormLoading}>
							Cancel
						</Button>
						<Button variant="primary" size="small" onClick={handleCreateWorkflow} disabled={workflowFormLoading}>
							{workflowFormLoading ? 'Creating…' : 'Create Workflow'}
						</Button>
					</div>
				}
			>
				<div className="add-checklist-form">
					{workflowFormError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{workflowFormError}
						</div>
					)}

					<div className="add-checklist-form__field">
						<label>Workflow Name <span className="add-checklist-form__required">*</span></label>
						<input
							type="text"
							placeholder="e.g., Standard Approval Workflow"
							value={workflowForm.name}
							onChange={(e) => setWorkflowForm((prev) => ({ ...prev, name: e.target.value }))}
							autoFocus
						/>
					</div>

					<div className="add-checklist-form__field">
						<label>Description</label>
						<textarea
							placeholder="Describe the purpose of this workflow..."
							value={workflowForm.description}
							onChange={(e) => setWorkflowForm((prev) => ({ ...prev, description: e.target.value }))}
							rows={2}
						/>
					</div>

					<WorkflowStepBuilder
						steps={workflowSteps}
						users={companyUsers}
						onChange={setWorkflowSteps}
						disabled={workflowFormLoading}
					/>
				</div>
			</Modal>

			{/* ── Add Checklist Modal (CM only) ─────────────────────────────────── */}
			<Modal
				isOpen={addModalOpen}
				onClose={() => setAddModalOpen(false)}
				title="Add Checklist Item"
				size="medium"
				footer={
					<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
						<Button variant="outline" size="small" onClick={() => setAddModalOpen(false)} disabled={addLoading}>
							Cancel
						</Button>
						<Button variant="primary" size="small" onClick={handleAddChecklist} disabled={addLoading}>
							{addLoading ? 'Submitting...' : 'Submit for PM Review'}
						</Button>
					</div>
				}
			>
				<form onSubmit={handleAddChecklist} className="add-checklist-form">
					{addError && (
						<div className="add-checklist-form__error">
							<AlertCircle size={14} />
							{addError}
						</div>
					)}

					<div className="add-checklist-form__field">
						<label>Checklist Name <span className="add-checklist-form__required">*</span></label>
						<input
							type="text"
							placeholder="Enter checklist item name"
							value={addForm.title}
							onChange={(e) => setAddForm((prev) => ({ ...prev, title: e.target.value }))}
							autoFocus
						/>
					</div>

					<div className="add-checklist-form__field">
						<label>Description</label>
						<textarea
							placeholder="Describe what this checklist item covers"
							value={addForm.description}
							onChange={(e) => setAddForm((prev) => ({ ...prev, description: e.target.value }))}
							rows={3}
						/>
					</div>

					{certificates.length > 0 && (
						<div className="add-checklist-form__field">
							<label>Certificate / Standard</label>
							<select
								value={addForm.certificate_id}
								onChange={(e) => setAddForm((prev) => ({ ...prev, certificate_id: e.target.value }))}
							>
								<option value="">— None —</option>
								{certificates.map((cert) => (
									<option key={cert.id} value={cert.id}>
										{cert.title || cert.name}
									</option>
								))}
							</select>
						</div>
					)}

					<div className="add-checklist-form__row">
						<div className="add-checklist-form__field">
							<label>Planned Start Date <span className="add-checklist-form__required">*</span></label>
							<input
								type="date"
								value={addForm.planned_start_date}
								onChange={(e) => setAddForm((prev) => ({ ...prev, planned_start_date: e.target.value }))}
							/>
						</div>
						<div className="add-checklist-form__field">
							<label>Planned End Date <span className="add-checklist-form__required">*</span></label>
							<input
								type="date"
								value={addForm.planned_end_date}
								min={addForm.planned_start_date || undefined}
								onChange={(e) => setAddForm((prev) => ({ ...prev, planned_end_date: e.target.value }))}
							/>
						</div>
					</div>

					<div className="add-checklist-form__field">
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
							<label style={{ marginBottom: 0 }}>Workflow (implementation steps)</label>
							<Button variant="ghost" size="small" type="button" onClick={handleOpenAddWorkflowModal}>
								<Plus size={12} />
								New Workflow
							</Button>
						</div>
						<select
							value={addForm.workflow_id}
							onChange={(e) => setAddForm((prev) => ({ ...prev, workflow_id: e.target.value }))}
							disabled={workflowsLoading}
						>
							<option value="">{workflowsLoading ? 'Loading workflows…' : '— No workflow —'}</option>
							{workflows.map((wf) => (
								<option key={wf.id} value={wf.id}>
									{buildWorkflowLabel(wf)}{wf.steps_count > 0 ? ` (${wf.steps_count} step${wf.steps_count !== 1 ? 's' : ''})` : ''}
								</option>
							))}
						</select>
						<span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
							Select the workflow that defines the implementation steps for this task.
						</span>
					</div>

					<div className="add-checklist-form__field">
						<label>Organization Document</label>
						<input
							type="text"
							placeholder="Search documents…"
							value={orgDocSearch}
							onChange={(e) => setOrgDocSearch(e.target.value)}
							style={{ marginBottom: '4px' }}
						/>
						<select
							value={addForm.organization_document_id}
							onChange={(e) => setAddForm((prev) => ({ ...prev, organization_document_id: e.target.value }))}
						>
							<option value="">— No document —</option>
							{orgDocuments
								.filter((d) => !orgDocSearch.trim() || d.title.toLowerCase().includes(orgDocSearch.toLowerCase()))
								.map((d) => (
									<option key={d.id} value={d.id}>{d.title}</option>
								))}
						</select>
						<span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
							Link a reference document from Company Settings.
						</span>
					</div>

					<div className="add-checklist-form__field add-checklist-form__field--checkbox">
						<label>
							<input
								type="checkbox"
								checked={addForm.evidence_required}
								onChange={(e) => setAddForm((prev) => ({ ...prev, evidence_required: e.target.checked }))}
							/>
							Evidence Required
						</label>
					</div>

					<div style={{ marginTop: '12px', padding: '10px 12px', background: 'var(--bg-secondary, #f8f9fa)', borderRadius: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
						This checklist item will be sent to the Project Manager for review before it appears in the project checklist.
					</div>
				</form>
			</Modal>
		</div>
	)
}

ChecklistsTab.propTypes = {
	checklists: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			title: PropTypes.string,
			item_text: PropTypes.string,
			workflow_status: PropTypes.string,
			due_date: PropTypes.string,
			planned_start_date: PropTypes.string,
			planned_end_date: PropTypes.string,
			assigned_to_name: PropTypes.string,
			certificate_name: PropTypes.string,
			certificate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			evidence_required: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
			notes_count: PropTypes.number,
			documents_count: PropTypes.number,
			workflow_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			approval_stage: PropTypes.string,
			pm_review_notes: PropTypes.string,
			management_review_notes: PropTypes.string,
		})
	).isRequired,
	project: PropTypes.object.isRequired,
	certificates: PropTypes.array,
	onItemUpdate: PropTypes.func.isRequired,
	canEdit: PropTypes.bool,
	canAddChecklist: PropTypes.bool,
	canApproveChecklist: PropTypes.bool,
	canPmReviewChecklist: PropTypes.bool,
	canManagementReviewChecklist: PropTypes.bool,
	canRevokeChecklist: PropTypes.bool,
	onRefresh: PropTypes.func,
}

ChecklistsTab.defaultProps = {
	certificates: [],
	canEdit: false,
	canAddChecklist: false,
	canApproveChecklist: false,
	canPmReviewChecklist: false,
	canManagementReviewChecklist: false,
	canRevokeChecklist: false,
	onRefresh: null,
}

export default ChecklistsTab
